import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { X, RotateCcw } from 'lucide-react';

interface ChatContainerProps {
    children: React.ReactNode;
    className?: string;
    isTyping?: boolean;
    onClose?: () => void;
    onRestart?: () => void;
    customStyles?: {
        primaryColor?: string;
        secondaryColor?: string;
    };
}

export const ChatContainer: React.FC<ChatContainerProps> = ({
    children,
    className,
    onClose,
    onRestart,
    customStyles,
}) => {
    const primaryColor = customStyles?.primaryColor || '#000000';
    const secondaryColor = customStyles?.secondaryColor || '#ffffff';
    const containerRef = useRef<HTMLDivElement>(null);
    const [spacerHeight, setSpacerHeight] = useState<number>(0);
    const [isAtBottom, setIsAtBottom] = useState<boolean>(true);

    // Calculate 50% of container height for initial spacing
    useEffect(() => {
        const updateSpacerHeight = () => {
            if (containerRef.current) {
                const height = containerRef.current.clientHeight;
                setSpacerHeight(height * 0.5);
            }
        };

        updateSpacerHeight();
        window.addEventListener('resize', updateSpacerHeight);

        return () => {
            window.removeEventListener('resize', updateSpacerHeight);
        };
    }, []);

    // Handle scroll to determine if at bottom
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            const scrolledToBottom = scrollHeight - scrollTop - clientHeight < 50;
            setIsAtBottom(scrolledToBottom);
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, []);

    // Auto-scroll to bottom when new messages arrive (if user is at bottom)
    useEffect(() => {
        if (!isAtBottom || !containerRef.current) return;

        const scrollToBottom = () => {
            if (containerRef.current) {
                // // console.log('[ChatContainer] Scrolling to bottom');
                containerRef.current.scrollTo({
                    top: containerRef.current.scrollHeight,
                    behavior: 'smooth',
                });
            }
        };

        // Check if there are any images that need to load
        const images = containerRef.current.querySelectorAll('img');
        const unloadedImages = Array.from(images).filter(img => !img.complete);

        // console.log('[ChatContainer] Images found:', images.length, 'Unloaded:', unloadedImages.length);

        if (unloadedImages.length > 0) {
            // console.log('[ChatContainer] Waiting for', unloadedImages.length, 'images to load');
            // Wait for all images to load before scrolling
            let loadedCount = 0;
            const targetCount = unloadedImages.length;

            const checkAndScroll = () => {
                loadedCount++;
                // console.log('[ChatContainer] Image loaded:', loadedCount, '/', targetCount);
                if (loadedCount === targetCount) {
                    // console.log('[ChatContainer] All images loaded, scrolling after delay');
                    // Small delay to ensure layout is updated
                    setTimeout(scrollToBottom, 100);
                }
            };

            unloadedImages.forEach(img => {
                if (img.complete) {
                    checkAndScroll();
                } else {
                    img.addEventListener('load', checkAndScroll, { once: true });
                    img.addEventListener('error', checkAndScroll, { once: true });
                }
            });
        } else {
            // No images to wait for, scroll immediately
            // console.log('[ChatContainer] No images to wait for, scrolling immediately');
            scrollToBottom();
        }
    }, [children, isAtBottom]);

    return (
        <div
            ref={containerRef}
            className={cn(
                'chat-scroll-container',
                'zentri-h-full zentri-overflow-y-auto zentri-overflow-x-hidden',
                'zentri-pl-4 zentri-pr-6 zentri-pb-4',
                className
            )}
        >
            {/* Dynamic spacer for 50% initial margin */}
            <div
                style={{ height: `${spacerHeight}px` }}
                className="zentri-flex-shrink-0"
                aria-hidden="true"
            />

            {/* Sticky Buttons - Close: primaryColor bg + secondaryColor icon; Reload: secondaryColor bg + primaryColor icon */}
            <div className="zentri-sticky zentri-top-3 zentri-z-50 zentri-flex zentri-justify-end zentri-gap-1.5 zentri-mb-3">
                {onRestart && (
                    <button
                        onClick={onRestart}
                        className="zentri-w-8 zentri-h-8 zentri-rounded-lg zentri-shadow-subtle zentri-flex zentri-items-center zentri-justify-center zentri-transition-all zentri-duration-300 zentri-ease-out hover:zentri-shadow-elegant hover:zentri-scale-110 active:zentri-scale-95"
                        style={{
                            backgroundColor: secondaryColor,
                            color: primaryColor,
                        }}
                        title="Restart conversation"
                    >
                        <RotateCcw size={16} strokeWidth={2} style={{ color: primaryColor }} />
                    </button>
                )}
                {onClose && (
                    <button
                        onClick={onClose}
                        className="zentri-w-8 zentri-h-8 zentri-rounded-lg zentri-shadow-subtle zentri-flex zentri-items-center zentri-justify-center zentri-transition-all zentri-duration-300 zentri-ease-out hover:zentri-shadow-elegant hover:zentri-scale-110 active:zentri-scale-95"
                        style={{
                            backgroundColor: primaryColor,
                            color: secondaryColor,
                        }}
                        aria-label="Close chat"
                    >
                        <X size={16} strokeWidth={2.5} style={{ color: secondaryColor }} />
                    </button>
                )}
            </div>

            {/* Chat messages */}
            {children}
        </div>
    );
};
